import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Lock, CheckCircle2 } from "lucide-react";
import { Logo } from "../components/Logo";
import { api, apiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { toast } from "sonner";

export default function ResetPassword() {
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const token = sp.get("token") || "";
  const [pwd, setPwd] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (pwd.length < 6) return setError("Le mot de passe doit contenir au moins 6 caractères");
    if (pwd !== confirm) return setError("Les deux mots de passe ne correspondent pas");
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, password: pwd });
      setDone(true); toast.success("Mot de passe mis à jour !");
    } catch (err) { setError(apiError(err)); } finally { setBusy(false); }
  };

  return (
    <div className="max-w-md mx-auto px-4 py-16">
      <div className="text-center mb-6"><Logo className="justify-center" /></div>
      <div className="bg-white rounded-3xl shadow-warm-lg p-7">
        {!token ? (
          <p className="text-center text-coral-twist" data-testid="reset-invalid">Lien invalide. Refaites une demande depuis « Mot de passe oublié ».</p>
        ) : done ? (
          <div className="text-center" data-testid="reset-done">
            <CheckCircle2 size={56} className="text-fresh-mint mx-auto" />
            <h1 className="font-display font-extrabold text-xl text-urban-teal mt-4">Mot de passe réinitialisé</h1>
            <p className="text-urban-teal/60 mt-2 text-sm">Vous pouvez maintenant vous connecter avec votre nouveau mot de passe.</p>
            <Button onClick={() => nav("/connexion")} className="rounded-full bg-street-gold text-white font-bold mt-6" data-testid="reset-login-btn">Se connecter</Button>
          </div>
        ) : (
          <>
            <h1 className="font-display font-extrabold text-2xl text-urban-teal">Nouveau mot de passe</h1>
            <p className="text-urban-teal/60 mt-1 text-sm">Choisissez un mot de passe sécurisé (6 caractères minimum).</p>
            <form onSubmit={submit} className="space-y-4 mt-5">
              <div>
                <label className="text-xs font-bold uppercase text-urban-teal/50">Nouveau mot de passe</label>
                <div className="flex items-center gap-2 border-2 border-cream-bg focus-within:border-urban-teal rounded-xl px-3 py-2.5 mt-1 transition-colors">
                  <Lock size={18} className="text-street-gold shrink-0" />
                  <input type="password" required value={pwd} onChange={(e) => setPwd(e.target.value)} placeholder="••••••••" className="w-full outline-none bg-transparent text-urban-teal" data-testid="reset-password" />
                </div>
              </div>
              <div>
                <label className="text-xs font-bold uppercase text-urban-teal/50">Confirmez le mot de passe</label>
                <div className="flex items-center gap-2 border-2 border-cream-bg focus-within:border-urban-teal rounded-xl px-3 py-2.5 mt-1 transition-colors">
                  <Lock size={18} className="text-street-gold shrink-0" />
                  <input type="password" required value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="••••••••" className="w-full outline-none bg-transparent text-urban-teal" data-testid="reset-confirm" />
                </div>
              </div>
              {error && <p className="text-sm text-coral-twist" data-testid="reset-error">{error}</p>}
              <Button type="submit" disabled={busy} className="w-full rounded-full h-12 font-bold text-white bg-street-gold hover:bg-street-gold/90" data-testid="reset-submit">{busy ? "Enregistrement..." : "Réinitialiser mon mot de passe"}</Button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
